import { useScorecardTemplates } from '@/components/shared/interview-scheduler/Scorecard/use-scorecard-templates';
import { Box, Center, Flex, Image, Spinner, Switch } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { LayoutLeft } from '@kula-ai/icons-core';
import scorecardIcon from '@/assets/svg/scorecard/scorecard-icon.svg';
import { FormBuilder } from '@/components/formBuilder';
import { components } from 'react-select';
import {
  ScorecardTemplateBuilder,
  type ScorecardTemplateBuilderHandle,
} from '@kula-ai/components';
import { ScorecardAddQuestion } from '@/components/shared/scorecard/ScorecardAddQuestion';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import {
  CUSTOM_TEMPLATE_LABEL,
  customTemplateFormatLabel,
} from '@/components/shared/customTemplateLabel';
import {
  isScorecardEdited,
  templateOptionToFormValues,
} from '@/components/shared/scorecardTemplateDiff';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { ScheduleAPI } from '@/components/shared/interview-scheduler/api';
import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useScorecardVisibilitySettings } from '@/api/scorecard-visibility';
import { SchFillWithAiField } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/useSchLocationField/SchFillWithAiField';
import { buildScorecardModel } from '@/components/shared/interview-scheduler/Scorecard/getScorecardModel';

interface ScorecardProps {
  interviewPlan?: any;
  note?: string;
  schedulerDetailsRef?: React.RefObject<any>;
  /** When true, scorecard step is visible; used to re-resolve Fill with AI context after Details form is filled (e.g. adhoc flow). */
  isScorecardStepActive?: boolean;
  toggleLabel?: string;
}
export const Scorecard = forwardRef(
  (
    {
      interviewPlan = {},
      note = 'This is the recommended scorecard template. You can customize it but changes will only apply to this interview.',
      schedulerDetailsRef,
      isScorecardStepActive = false,
      toggleLabel = 'Include scorecards in this interview',
    }: ScorecardProps,
    ref
  ) => {
    const formRef = useRef<ScorecardTemplateBuilderHandle>(null);
    // Relabels the picker to "Custom" once the questions leave the template.
    const [templateEdited, setTemplateEdited] = useState(false);
    const { currentJob: { data: currentJobData = {} } = {} } =
      useSelector(getAtsJobCreate);
    const { basic_info = {} } = currentJobData;
    const { department = {}, employment_type = '', office = [] } = basic_info;
    const defaultScorecardParams = {
      department_ids: [department?.id],
      employment_types: [employment_type],
      office_ids: office?.map((item) => item?.id),
    };
    const containerRef = useRef(null);
    const parseScorecardRequired = (value: unknown): boolean => {
      if (value === null || value === undefined) return true;
      return value !== false && value !== 'false';
    };
    const [scorecardRequired, setScorecardRequired] = useState(
      parseScorecardRequired(interviewPlan?.scorecard_required)
    );
    const [prevScorecardProp, setPrevScorecardProp] = useState(
      interviewPlan?.scorecard_required
    );
    if (interviewPlan?.scorecard_required !== prevScorecardProp) {
      setPrevScorecardProp(interviewPlan?.scorecard_required);
      setScorecardRequired(
        parseScorecardRequired(interviewPlan?.scorecard_required)
      );
    }
    const { scorecard_config = {} } = interviewPlan ?? {};
    const { data: defaultScoreCard, isLoading } = useQuery({
      queryKey: ['getDefaultScorecard', defaultScorecardParams],
      queryFn: () =>
        ScheduleAPI?.getDefaultScorecard({
          payload: defaultScorecardParams,
        }),
      enabled: !interviewPlan?.id,
    });

    const scorecard = {
      scorecard_config: interviewPlan?.scorecard_config,
      scorecard_rating_description:
        interviewPlan?.scorecard_rating_description ||
        scorecard_config?.rating_description,
      scorecard_feedback_description:
        interviewPlan?.scorecard_feedback_description ||
        scorecard_config?.feedback_description,
      is_feedback_mandatory:
        interviewPlan?.scorecard_config?.is_feedback_mandatory ??
        scorecard_config?.is_feedback_mandatory ??
        false,
    };

    const scorecardDets = interviewPlan?.id
      ? scorecard
      : defaultScoreCard?.questions?.length
        ? defaultScoreCard
        : [];

    const {
      templateOptions,
      initialValues,
      changeTemplateInProgress,
      handleTemplateOnChange,
      selectedTemplateValue,
      selectedTemplateOption,
      isCustomScorecard,
      isManualPick,
    } = useScorecardTemplates(
      scorecardDets?.scorecard_config?.questions,
      scorecardDets
    );

    // A content diff, not a dirty flag — undoing an edit restores the name.
    // Diffed against the template only when the user picked it here. A
    // template recognised from saved questions is a *copy*, and a copy carries
    // its own feedback/rating copy — comparing those would report every
    // reopened scorecard as edited and relabel the picker "Custom" on open.
    const diffBaseline = useMemo(
      () =>
        isManualPick
          ? templateOptionToFormValues(selectedTemplateOption as any)
          : initialValues,
      [isManualPick, selectedTemplateOption, initialValues]
    );

    const handleBuilderChange = useCallback(
      (values: any) => {
        setTemplateEdited(isScorecardEdited(values, diffBaseline as any));
      },
      [diffBaseline]
    );

    const { data: scorecardVisibilityData } = useScorecardVisibilitySettings();
    const fillWithAiAccountEnabled =
      scorecardVisibilityData?.data?.account_setting?.fill_with_ai_enabled;

    // Resolve form ref and visibility in effect to avoid "Cannot access ref value during render"
    const [fillWithAiContext, setFillWithAiContext] = useState<{
      formRef: React.RefObject<any>;
      updateSharedData: ((_: string, __: any) => void) | undefined;
      showBlock: boolean;
    } | null>(null);

    useEffect(() => {
      const resolveFillWithAiContext = () => {
        if (!schedulerDetailsRef || !fillWithAiAccountEnabled) return;
        const formRef = schedulerDetailsRef.current?.getFormRef?.();
        const updateSharedData =
          schedulerDetailsRef.current?.getUpdateSharedData?.();
        if (!formRef?.current?.values) return;
        const { interviewers, location, note_taker_enabled } =
          formRef.current.values ?? {};
        const isAtLeastOneInterviewer = Array.isArray(interviewers)
          ? interviewers?.length > 0
          : !!interviewers;
        const isValidLocation = ['zoom', 'google_meet', 'teams'].includes(
          location
        );
        const showBlock =
          isAtLeastOneInterviewer &&
          isValidLocation &&
          note_taker_enabled === true;
        setFillWithAiContext({
          formRef,
          updateSharedData: updateSharedData ?? undefined,
          showBlock,
        });
      };

      const id = setTimeout(resolveFillWithAiContext, 0);
      return () => clearTimeout(id);
    }, [
      schedulerDetailsRef,
      fillWithAiAccountEnabled,
      interviewPlan?.id,
      isScorecardStepActive,
    ]);

    const showFillWithAiBlock = fillWithAiContext?.showBlock ?? false;
    const schedulerFormRef = fillWithAiContext?.formRef;
    const updateSharedData = fillWithAiContext?.updateSharedData;

    const CustomValueContainer = ({ children, ...props }) => (
      <components.ValueContainer {...props}>
        <Flex alignItems="center" gap="4px">
          <LayoutLeft
            size="1em"
            color="var(--kula-colors-secondary-700)"
            className="kula-icon"
          />
          <Box>{children}</Box>
        </Flex>
      </components.ValueContainer>
    );
    const formConfig = [
      {
        name: 'scorecard_template',
        type: 'kSelect',
        // A scorecard whose questions match no offered template is a custom
        // one; say so rather than showing the empty picker.
        placeholder: isCustomScorecard
          ? CUSTOM_TEMPLATE_LABEL
          : 'Select scorecard template',
        options: templateOptions,
        // Rendered from state: the picker's Formik mounts after the templates
        // load, so an imperative seed can land on a null ref.
        defaultValue: selectedTemplateValue,
        customFormatLabel: customTemplateFormatLabel(templateEdited),
        components: {
          IndicatorSeparator: () => null,
          ValueContainer: CustomValueContainer,
        },
        customOnChange: handleTemplateOnChange,
      },
    ];

    const scrollToError = () => {
      setTimeout(() => {
        const errorElement = document.querySelector(
          '.chakra-form__error-message, [aria-invalid="true"]'
        );
        if (errorElement) {
          const container =
            errorElement.closest('[draggable="true"]') || errorElement;
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    };

    const getModel = async () => {
      if (!scorecardRequired) {
        return buildScorecardModel(false, true);
      }

      const isValid = (await formRef.current?.trigger()) ?? false;

      if (!isValid) {
        scrollToError();
      }

      return buildScorecardModel(
        true,
        isValid,
        formRef.current?.getValues(),
        Number(selectedTemplateValue) || null
      );
    };

    useImperativeHandle(ref, () => ({
      getModel,
    }));

    return (
      <Flex
        p="16px 24px"
        flexDir="column"
        overflowX="hidden"
        data-test-id="section-scorecard"
        ref={containerRef}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          p="16px"
          bg="secondary-25"
          borderRadius="6px"
          mb={scorecardRequired ? '12px' : '0'}
        >
          <Flex alignItems="center" gap="12px">
            <Image
              src={scorecardIcon}
              alt=""
              width="35px"
              height="45px"
              flexShrink={0}
            />
            <KText size="sm-semi-bold" color="secondary-900">
              {toggleLabel}
            </KText>
          </Flex>
          <Switch
            className={`ats-switch-toogle ${scorecardRequired ? 'active' : ''}`}
            colorScheme="ats-primary"
            isChecked={scorecardRequired}
            onChange={(e) => setScorecardRequired(e.target.checked)}
          />
        </Flex>
        <Flex
          flexDir="column"
          gap="12px"
          h={scorecardRequired ? 'auto' : '0'}
          overflow={scorecardRequired ? 'visible' : 'hidden'}
        >
          {showFillWithAiBlock && schedulerFormRef && (
            <SchFillWithAiField
              schedulerFormRef={schedulerFormRef}
              updateSharedData={updateSharedData}
              interviewPlan={interviewPlan}
            />
          )}
          <FormBuilder config={formConfig} formRef={null}></FormBuilder>
          <KText
            borderWidth="1px"
            borderColor="secondary-100"
            borderRadius="xs"
            bg="secondary-25"
            p="12px"
          >
            <b>Note:</b>
            {note}
          </KText>
          <ScorecardAddQuestion formRef={formRef} />
          {changeTemplateInProgress || isLoading ? (
            <Center h="50vh" flex="1">
              <Spinner />
            </Center>
          ) : (
            <ScorecardTemplateBuilder
              key={JSON.stringify(initialValues)}
              formRef={formRef}
              initialValues={initialValues}
              onChange={handleBuilderChange}
              // 0 questions is a valid scorecard here (matches the legacy
              // Formik builder, which never enforced a minimum). Suppresses
              // the builder's "At least one question is required" empty state.
              minQuestions={0}
            />
          )}
        </Flex>
      </Flex>
    );
  }
);

Scorecard.displayName = 'Scorecard';
